import { useTranslation } from '@infra/i18n';
import { Badge } from '@shared/ui';

export const CONTENT_VISIBILITY = {
  SYSTEM: 'system',
  ORGANIZATION: 'organization',
  PERSONAL: 'personal',
} as const;

export type ContentVisibility = (typeof CONTENT_VISIBILITY)[keyof typeof CONTENT_VISIBILITY];

/** What an org admin may choose when creating content: never system. */
export type OrgCreatableVisibility = Exclude<ContentVisibility, 'system'>;

const VISIBILITY_VALUES: readonly ContentVisibility[] = Object.values(CONTENT_VISIBILITY);

export function isContentVisibility(value: unknown): value is ContentVisibility {
  return typeof value === 'string' && (VISIBILITY_VALUES as readonly string[]).includes(value);
}

/** Super admin content filter: every scope, system included. */
export const ADMIN_VISIBILITY_FILTERS = [
  'all',
  CONTENT_VISIBILITY.SYSTEM,
  CONTENT_VISIBILITY.ORGANIZATION,
  CONTENT_VISIBILITY.PERSONAL,
] as const;

/** Org content filter. "System" is not a choice; an unknown URL value falls back to all. */
export const ORG_VISIBILITY_FILTERS = [
  'all',
  CONTENT_VISIBILITY.ORGANIZATION,
  CONTENT_VISIBILITY.PERSONAL,
] as const;

/** Scope badge. Renders nothing when the record has no visibility yet. */
export function VisibilityBadge({ visibility }: { visibility?: string | null }) {
  const { t } = useTranslation();
  if (!isContentVisibility(visibility)) return null;
  return (
    <Badge variant="outline" data-testid={`visibility-${visibility}`}>
      {t(`common:visibility.${visibility}`)}
    </Badge>
  );
}
